import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useCheckupsStore } from '../store'
import { usePetsStore } from '../../pets/store'
import type { CheckupCreate, CheckupType } from '@rondela/shared'

export function CheckupFormPage() {
  const { petId, id } = useParams<{ petId: string; id?: string }>()
  const navigate = useNavigate()
  const { checkups, isLoading, error, fetchCheckupsByPet, createCheckup, updateCheckup, clearError } = useCheckupsStore()
  const { currentPet, fetchPet } = usePetsStore()
  const isEditing = !!id

  const [formData, setFormData] = useState<CheckupCreate>({
    tipo: 'rutina',
    fecha: new Date().toISOString().split('T')[0] ?? '',
    hora: '',
    veterinario: '',
    clinica: '',
    motivo: '',
    diagnostico: '',
    tratamiento: '',
    notas: '',
    proximaCita: '',
  })

  useEffect(() => {
    if (petId) {
      fetchPet(petId)
      fetchCheckupsByPet(petId)
    }
    return () => clearError()
  }, [petId, fetchPet, fetchCheckupsByPet, clearError])

  useEffect(() => {
    if (id && checkups.length > 0) {
      const checkup = checkups.find((c) => c.id === id)
      if (checkup) {
        setFormData({
          tipo: checkup.tipo,
          fecha: checkup.fecha.split('T')[0] ?? '',
          hora: checkup.hora ?? '',
          veterinario: checkup.veterinario ?? '',
          clinica: checkup.clinica ?? '',
          motivo: checkup.motivo,
          diagnostico: checkup.diagnostico ?? '',
          tratamiento: checkup.tratamiento ?? '',
          notas: checkup.notas ?? '',
          proximaCita: checkup.proximaCita?.split('T')[0] ?? '',
        })
      }
    }
  }, [id, checkups])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!petId) return

    try {
      if (isEditing && id) {
        await updateCheckup(id, formData)
      } else {
        await createCheckup(petId, formData)
      }
      navigate(`/mascotas/${petId}`)
    } catch {
      // Error already handled in store
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const typeOptions: { value: CheckupType; label: string }[] = [
    { value: 'rutina', label: 'Rutina' },
    { value: 'emergencia', label: 'Emergencia' },
    { value: 'seguimiento', label: 'Seguimiento' },
    { value: 'especialista', label: 'Especialista' },
  ]

  return (
    <div className="page">
      <div className="page-header">
        <Link to={`/mascotas/${petId}`} className="btn btn-ghost">
          &larr; Volver
        </Link>
        <h1>
          {isEditing ? 'Editar control' : 'Nuevo control'}
          {currentPet && <span className="text-muted"> - {currentPet.nombre}</span>}
        </h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="tipo">Tipo de control *</label>
            <select
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              className="form-control"
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="fecha">Fecha *</label>
            <input
              type="date"
              id="fecha"
              name="fecha"
              value={formData.fecha}
              onChange={handleChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hora">Hora</label>
            <input
              type="time"
              id="hora"
              name="hora"
              value={formData.hora}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="motivo">Motivo de la visita *</label>
          <input
            type="text"
            id="motivo"
            name="motivo"
            value={formData.motivo}
            onChange={handleChange}
            required
            className="form-control"
            placeholder="Ej: Revisión anual, Vacunación, Síntomas digestivos..."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="veterinario">Veterinario</label>
            <input
              type="text"
              id="veterinario"
              name="veterinario"
              value={formData.veterinario}
              onChange={handleChange}
              className="form-control"
              placeholder="Dr./Dra. ..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="clinica">Clínica</label>
            <input
              type="text"
              id="clinica"
              name="clinica"
              value={formData.clinica}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="diagnostico">Diagnóstico</label>
          <textarea
            id="diagnostico"
            name="diagnostico"
            value={formData.diagnostico}
            onChange={handleChange}
            rows={3}
            className="form-control"
            placeholder="Resultado del examen..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="tratamiento">Tratamiento</label>
          <textarea
            id="tratamiento"
            name="tratamiento"
            value={formData.tratamiento}
            onChange={handleChange}
            rows={3}
            className="form-control"
            placeholder="Medicamentos recetados, indicaciones..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="notas">Notas adicionales</label>
          <textarea
            id="notas"
            name="notas"
            value={formData.notas}
            onChange={handleChange}
            rows={2}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label htmlFor="proximaCita">Próxima cita</label>
          <input
            type="date"
            id="proximaCita"
            name="proximaCita"
            value={formData.proximaCita}
            onChange={handleChange}
            className="form-control"
          />
          <p className="form-help">Fecha recomendada para el siguiente control</p>
        </div>

        <div className="form-actions">
          <Link to={`/mascotas/${petId}`} className="btn btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Registrar control'}
          </button>
        </div>
      </form>
    </div>
  )
}
